<template>
  <div class="analytics-container">
    <div class="analytics-grid">
      <div class="analytics-card">
        <div class="card-header">
          <h3 class="card-title">Lessons Overview</h3>
          <div class="card-actions">
            <button @click="refreshAnalytics" class="refresh-button" title="Refresh Analytics">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="refresh-icon">
                <path d="M23 4v6h-6M1 20v-6h6"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
          </div>
        </div>
        
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-value">{{ totalLessons }}</div>
            <div class="stat-label">Total Lessons</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">{{ totalDuration }} min</div>
            <div class="stat-label">Total Duration</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">{{ completedLessons }}</div>
            <div class="stat-label">Completed</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">{{ inProgressLessons }}</div>
            <div class="stat-label">In Progress</div>
          </div>
        </div>
      </div>
      
      <div class="analytics-card">
        <div class="card-header">
          <h3 class="card-title">Status Distribution</h3>
        </div>
        <div class="chart-container">
          <canvas ref="statusChart"></canvas>
        </div>
      </div>
      
      <div class="analytics-card">
        <div class="card-header">
          <h3 class="card-title">Course Distribution</h3>
        </div>
        <div class="chart-container">
          <canvas ref="courseChart"></canvas>
        </div>
      </div>
      
      <div class="analytics-card">
        <div class="card-header">
          <h3 class="card-title">Recent Activity</h3>
        </div>
        <div class="activity-list">
          <div 
            v-for="activity in recentActivity" 
            :key="activity.id" 
            class="activity-item"
          >
            <div class="activity-icon" :class="activity.type">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="icon">
                <path v-if="activity.type === 'play'" d="M5 3l14 9-14 9V3z"/>
                <path v-if="activity.type === 'edit'" d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                <path v-if="activity.type === 'create'" d="M12 5v14M5 12h14"/>
                <path v-if="activity.type === 'delete'" d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </div>
            <div class="activity-content">
              <div class="activity-title">{{ activity.title }}</div>
              <div class="activity-meta">
                <span class="activity-time">{{ formatTime(activity.timestamp) }}</span>
                <span class="activity-course">{{ activity.course }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';

export default {
  name: 'LessonsAnalytics',
  props: {
    filteredLessons: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      statusChart: null,
      courseChart: null,
      recentActivity: []
    }
  },
  computed: {
    totalLessons() {
      return this.filteredLessons.length;
    },
    totalDuration() {
      return this.filteredLessons.reduce((sum, lesson) => sum + (lesson.duration || 0), 0);
    },
    completedLessons() {
      return this.filteredLessons.filter(lesson => lesson.lesson_status === 'completed').length;
    },
    inProgressLessons() {
      return this.filteredLessons.filter(lesson => lesson.lesson_status === 'in_progress').length;
    }
  },
  methods: {
    refreshAnalytics() {
      this.$emit('refresh-analytics');
    },
    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleString();
    },
    initCharts() {
      // Status Distribution Chart
      const statusCtx = this.$refs.statusChart.getContext('2d');
      const statusData = {
        labels: ['Not Started', 'In Progress', 'Completed', 'Pending', 'Uploaded'],
        datasets: [{
          data: [
            this.filteredLessons.filter(l => l.lesson_status === 'not_started').length,
            this.filteredLessons.filter(l => l.lesson_status === 'in_progress').length,
            this.filteredLessons.filter(l => l.lesson_status === 'completed').length,
            this.filteredLessons.filter(l => l.lesson_status === 'pending').length,
            this.filteredLessons.filter(l => l.lesson_status === 'uploaded').length
          ],
          backgroundColor: [
            '#94a3b8',
            '#3b82f6',
            '#10b981',
            '#f59e0b',
            '#8b5cf6'
          ]
        }]
      };
      
      this.statusChart = new Chart(statusCtx, {
        type: 'doughnut',
        data: statusData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'right'
            }
          }
        }
      });
      
      // Course Distribution Chart
      const courseCtx = this.$refs.courseChart.getContext('2d');
      const courseCounts = {};
      
      this.filteredLessons.forEach(lesson => {
        const course = lesson.course?.title || 'Unassigned';
        courseCounts[course] = (courseCounts[course] || 0) + 1;
      });
      
      const courseData = {
        labels: Object.keys(courseCounts),
        datasets: [{
          data: Object.values(courseCounts),
          backgroundColor: [
            '#3b82f6',
            '#10b981',
            '#f59e0b',
            '#8b5cf6',
            '#ec4899',
            '#6366f1',
            '#14b8a6',
            '#f97316'
          ]
        }]
      };
      
      this.courseChart = new Chart(courseCtx, {
        type: 'bar',
        data: courseData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1
              }
            }
          }
        }
      });
    }
  },
  mounted() {
    this.initCharts();
  },
  watch: {
    filteredLessons: {
      handler() {
        if (this.statusChart && this.courseChart) {
          this.statusChart.destroy();
          this.courseChart.destroy();
          this.initCharts();
        }
      },
      deep: true
    }
  },
  beforeUnmount() {
    if (this.statusChart) {
      this.statusChart.destroy();
    }
    if (this.courseChart) {
      this.courseChart.destroy();
    }
  }
}
</script>

<style scoped>
.analytics-container {
  padding: 1.5rem;
}

.analytics-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.analytics-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.refresh-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-button:hover {
  background: #e2e8f0;
  transform: rotate(180deg);
}

.refresh-icon {
  width: 16px;
  height: 16px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

.stat-item {
  text-align: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 6px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #64748b;
}

.chart-container {
  height: 300px;
  position: relative;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.activity-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.activity-item:hover {
  background: #f1f5f9;
  transform: translateX(4px);
}

.activity-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  flex-shrink: 0;
}

.activity-icon.play {
  background: #dbeafe;
  color: #1d4ed8;
}

.activity-icon.edit {
  background: #dcfce7;
  color: #166534;
}

.activity-icon.create {
  background: #fef3c7;
  color: #9a3412;
}

.activity-icon.delete {
  background: #fee2e2;
  color: #b91c1c;
}

.icon {
  width: 16px;
  height: 16px;
}

.activity-content {
  flex: 1;
}

.activity-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.activity-meta {
  display: flex;
  gap: 1rem;
  font-size: 0.75rem;
  color: #64748b;
}

@media (max-width: 1200px) {
  .analytics-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .analytics-container {
    padding: 1rem;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
}
</style> 